import * as Sentry from "@sentry/react";
import { useContext, useEffect } from "react";
import { sessionStore } from "../../contexts/SessionContext";
import type Session from "../../interfaces/Session";
import { LoaderStatus } from "../../interfaces/Status";
import * as localStorage from "../../utils/localStorage";

const useSetSentryContext = () => {
  const { session } = useContext(sessionStore);
  useEffect(() => {
    Sentry.setUser(session.userId ? { id: session.userId } : null);
    Sentry.setTag("user.type", session.userType);
    Sentry.setTag("app", session.app);
    Sentry.setTag("app.version", session.version);

    const sessionInfo = prepareSessionForSentryContext(session);
    const collectionsInfo = prepareSessionCollectionsForSentryContext(session);
    const handleStorageUpdate = () => {
      const localStorageFlags = getStorageFlags();
      Sentry.setContext("Session", Object.fromEntries(sessionInfo));
      // Large collections in their own context so their size can't evict the
      // core Session keys (Sentry caps each context and drops overflow).
      Sentry.setContext(
        "Session Collections",
        collectionsInfo.size ? Object.fromEntries(collectionsInfo) : null,
      );
      Sentry.setContext("Flags", { flags: localStorageFlags });
    };
    window.addEventListener("storage", handleStorageUpdate);
    handleStorageUpdate();
    return () => {
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, [session]);
};
export default useSetSentryContext;

type SessionKeys = keyof Required<Session>;

/**
 * Which context to add the session key to
 */
enum SessionDestination {
  Session,
  SessionCollections,
  None,
}

/**
 * Describes how each key of the session context is parsed to be added to the
 * First param is which context to add the key to, second is an optional parsing function
 */
const sessionLoggerPrepare: {
  [key in SessionKeys]: [
    SessionDestination,
    ((item: any) => string) | undefined,
  ];
} = {
  sessionId: [SessionDestination.Session, undefined],
  userId: [SessionDestination.Session, undefined],
  firstname: [SessionDestination.Session, undefined],
  lastname: [SessionDestination.Session, undefined],
  userProvider: [SessionDestination.Session, undefined],
  appProvider: [SessionDestination.Session, undefined],
  appVariation: [SessionDestination.None, undefined],
  userType: [SessionDestination.Session, undefined],
  evidencebId: [SessionDestination.None, undefined],
  version: [SessionDestination.Session, undefined],
  extra: [SessionDestination.Session, JSON.stringify],
  theme: [SessionDestination.Session, undefined],
  flags: [SessionDestination.None, undefined],
  dismissMessages: [SessionDestination.None, undefined],
  fragments: [SessionDestination.Session, undefined],
  specimen: [SessionDestination.None, undefined],
  app: [SessionDestination.Session, undefined],
  soloAI: [SessionDestination.None, undefined],
  statementsFetched: [SessionDestination.None, undefined],
  statements: [SessionDestination.None, undefined], // too large
  athenaEvents: [SessionDestination.None, undefined], // too large
  prLockStatus: [
    SessionDestination.SessionCollections,
    (prLockStatus) => {
      if (prLockStatus?.status !== LoaderStatus.Success)
        return prLockStatus?.status ?? "";
      return JSON.stringify(prLockStatus.payload);
    },
  ],
  duos: [SessionDestination.None, undefined], // noisy, low value
  resourceAssignments: [SessionDestination.None, undefined], // noisy, low value
  school: [SessionDestination.SessionCollections, JSON.stringify],
  classrooms: [SessionDestination.SessionCollections, JSON.stringify],
  groups: [SessionDestination.SessionCollections, JSON.stringify],
  classroomIds: [SessionDestination.SessionCollections, JSON.stringify],
  experimentationTests: [SessionDestination.SessionCollections, JSON.stringify],
};

const buildContext = (session: Session, collectionType: SessionDestination) => {
  const prepared: Map<string, any> = new Map();
  for (const key in session) {
    const [destination, prepareFn] = sessionLoggerPrepare[key as SessionKeys];
    if (destination !== collectionType) continue;
    const value: any = session[key as keyof Session];
    if (prepareFn === undefined) prepared.set(key, value);
    else prepared.set(key, prepareFn(value));
  }
  return prepared;
};

const prepareSessionForSentryContext = (session: Session) =>
  buildContext(session, SessionDestination.Session);

const prepareSessionCollectionsForSentryContext = (session: Session) =>
  buildContext(session, SessionDestination.SessionCollections);

const getStorageFlags = (): string[] => {
  return localStorage.getItem<string[]>(localStorage.Key.DEV_FLAGS) ?? [];
};
